import React, { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { toast } from 'sonner';
import {
  ArrowLeft, RotateCcw, Eye, FileText, FileSpreadsheet, FileType2,
  ChevronRight, ChevronDown, Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerReportBuilder = ({ customerId = null, customerName = '', onBack = null }) => {
  const { token, user } = useContext(AuthContext);
  const h = { headers: { Authorization: `Bearer ${token}` } };

  const [customers, setCustomers] = useState([]);
  const [cid, setCid] = useState(customerId || '');
  const [schema, setSchema] = useState([]);           // [{key,label,fields:[{key,label}]}]
  const [selSections, setSelSections] = useState(new Set());
  const [selFields, setSelFields] = useState({});     // section -> Set(fieldKeys) ; absent = all
  const [expanded, setExpanded] = useState(new Set());
  const [locations, setLocations] = useState([]);
  const [selProps, setSelProps] = useState(new Set()); // empty = all
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState('');
  const [specLang, setSpecLang] = useState('en');

  // Load customer list only when no preselected customer (general Reports entry).
  useEffect(() => {
    if (!customerId) axios.get(`${API}/customers`, h).then((r) => setCustomers(r.data || [])).catch(() => {});
    // eslint-disable-next-line
  }, [customerId]);

  // Load section schema once.
  useEffect(() => {
    axios.get(`${API}/customer-reports/sections`, h)
      .then((r) => {
        const secs = r.data.sections || [];
        setSchema(secs);
        setSelSections(new Set(secs.map((s) => s.key)));  // default: all sections on
      })
      .catch(() => toast.error('No report access'));
    // eslint-disable-next-line
  }, [token]);

  // Load this customer's properties for the Properties sub-list.
  useEffect(() => {
    if (!cid) { setLocations([]); return; }
    axios.get(`${API}/customers/${cid}/locations`, h).then((r) => setLocations(r.data || [])).catch(() => setLocations([]));
    setPreview(null); setSelProps(new Set());
    // eslint-disable-next-line
  }, [cid, token]);

  const allOn = schema.length > 0 && selSections.size === schema.length;
  const toggleAll = () => setSelSections(allOn ? new Set() : new Set(schema.map((s) => s.key)));
  const toggleSection = (k) => setSelSections((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleExpand = (k) => setExpanded((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const fieldOn = (sk, fk) => !selFields[sk] || selFields[sk].has(fk);
  const toggleField = (sk, fk, allFieldKeys) => setSelFields((m) => {
    const cur = m[sk] ? new Set(m[sk]) : new Set(allFieldKeys);
    cur.has(fk) ? cur.delete(fk) : cur.add(fk);
    const next = { ...m };
    if (cur.size === allFieldKeys.length) delete next[sk]; else next[sk] = cur;
    return next;
  });
  const toggleProp = (id) => setSelProps((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const buildBody = () => {
    const fields = {};
    Object.entries(selFields).forEach(([k, set]) => { if (selSections.has(k)) fields[k] = [...set]; });
    return {
      sections: [...selSections],
      fields,
      property_ids: selProps.size ? [...selProps] : null,
      spec_language: specLang,
    };
  };

  const resetAll = () => {
    setSelSections(new Set(schema.map((s) => s.key)));
    setSelFields({}); setExpanded(new Set()); setSelProps(new Set()); setPreview(null);
    toast.success('Report reset');
  };

  const generate = useCallback(async () => {
    if (!cid) { toast.error('Select a customer first'); return; }
    if (selSections.size === 0) { toast.error('Select at least one section'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/customer-reports/${cid}/build`, buildBody(), h);
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
    setBusy(false);
    // eslint-disable-next-line
  }, [cid, selSections, selFields, selProps, specLang, token]);

  const doExport = async (fmt) => {
    if (!preview) return;
    setExporting(fmt);
    try {
      const r = await axios.post(`${API}/customer-reports/${cid}/export?format=${fmt}`, buildBody(),
        { ...h, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      const ext = fmt === 'xlsx' ? 'xlsx' : fmt === 'docx' ? 'docx' : 'pdf';
      a.href = url; a.download = `${(preview.customer_name || 'customer').replace(/\s+/g, '_')}_report.${ext}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
    setExporting('');
  };

  const sectionCount = (b) => b.type === 'table' ? b.rows.length : b.type === 'groups' ? b.groups.length : b.type === 'spec_docs' ? b.entries.length : null;

  const inputCls = 'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm';

  return (
    <div className="p-4 md:p-6" data-testid="customer-report-builder">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          {onBack && (
            <button onClick={onBack} className="mb-2 inline-flex items-center gap-1 text-sm text-slate-500" data-testid="report-back">
              <ArrowLeft size={15} /> Back
            </button>
          )}
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">Insight</div>
          <h1 className="text-3xl font-extrabold text-[#0E2A47]">Customer Report Builder</h1>
          <p className="mt-1 max-w-xl text-sm text-slate-500">Compile customer, property, budget and intelligence data into a shareable report. Preview on screen, then export.</p>
        </div>
        <button onClick={resetAll} data-testid="report-reset-btn"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-[#C8641E] px-3 py-2 text-sm font-semibold text-[#C8641E] hover:bg-orange-50">
          <RotateCcw size={15} /> Reset
        </button>
      </div>

      <div className="grid gap-5 lg:grid-cols-[360px_1fr]">
        {/* Builder */}
        <div className="min-w-0 space-y-5 rounded-2xl border border-slate-200 bg-white p-5">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">1 — Report Source</div>
            <div className="mt-1 rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-[#0E2A47]">Customers</div>
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">2 — Customer</div>
            {customerId ? (
              <div className="mt-1 rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-[#0E2A47]" data-testid="report-customer-fixed">{customerName}</div>
            ) : (
              <select className={`${inputCls} mt-1`} value={cid} onChange={(e) => setCid(e.target.value)} data-testid="report-customer-select">
                <option value="">Select a customer…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">3 — Report Sections</div>
            <label className="mt-2 flex items-center gap-2 text-sm font-semibold text-[#0E2A47]">
              <input type="checkbox" checked={allOn} onChange={toggleAll} data-testid="report-section-all" /> All of the Above
            </label>
            <div className="mt-1 space-y-1">
              {schema.map((s) => {
                const on = selSections.has(s.key);
                const open = expanded.has(s.key);
                const allFieldKeys = s.fields.map((f) => f.key);
                return (
                  <div key={s.key} className="rounded-lg">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input type="checkbox" checked={on} onChange={() => toggleSection(s.key)} data-testid={`report-section-${s.key}`} /> {s.label}
                      </label>
                      {s.fields.length > 0 && (
                        <button onClick={() => toggleExpand(s.key)} className="flex items-center gap-0.5 text-xs text-slate-400 hover:text-slate-600" data-testid={`report-fields-toggle-${s.key}`}>
                          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Fields {selFields[s.key] ? `(${selFields[s.key].size})` : '(all)'}
                        </button>
                      )}
                    </div>
                    {open && on && (
                      <div className="ml-6 mt-1 flex flex-wrap gap-1.5" data-testid={`report-fields-${s.key}`}>
                        {s.fields.map((f) => {
                          const fon = fieldOn(s.key, f.key);
                          return (
                            <button key={f.key} type="button" onClick={() => toggleField(s.key, f.key, allFieldKeys)}
                              className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${fon ? 'border-[#0E2A47] bg-[#0E2A47] text-white' : 'border-slate-200 bg-white text-slate-400 line-through'}`}>{f.label}</button>
                          );
                        })}
                      </div>
                    )}
                    {/* Cleaning specs language selector */}
                    {open && on && s.key === 'cleaning_specs' && (
                      <div className="ml-6 mt-2 flex items-center gap-2 rounded-lg bg-slate-50 p-2" data-testid="report-spec-language">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Language</span>
                        {[['en', 'English'], ['es', 'Español']].map(([code, lbl]) => (
                          <button key={code} type="button" onClick={() => setSpecLang(code)} data-testid={`report-spec-lang-${code}`}
                            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${specLang === code ? 'bg-[#0E2A47] text-white' : 'border border-slate-200 bg-white text-slate-500'}`}>{lbl}</button>
                        ))}
                      </div>
                    )}
                    {/* Properties sub-list (none checked = all) */}
                    {open && on && s.key === 'properties' && locations.length > 0 && (
                      <div className="ml-6 mt-2 rounded-lg bg-slate-50 p-2" data-testid="report-properties-list">
                        <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Properties (none checked = all)</div>
                        {locations.map((l) => (
                          <label key={l.id} className="flex items-center gap-2 py-0.5 text-xs text-slate-600">
                            <input type="checkbox" checked={selProps.has(l.id)} onChange={() => toggleProp(l.id)} data-testid={`report-prop-${l.id}`} />
                            <span className="truncate">{l.location_name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <button onClick={generate} disabled={busy || !cid} data-testid="report-generate-btn"
            className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#5FA718] px-3 py-2.5 text-sm font-semibold text-white hover:bg-[#4f8d14] disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />} Generate Preview
          </button>
        </div>

        {/* Preview */}
        <div className="min-w-0">
          {!preview ? (
            <div className="flex min-h-[420px] items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 text-center text-sm text-slate-400" data-testid="report-preview-empty">
              Select a customer and sections, then Generate Preview to review the report here — no download required.
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="report-preview">
              <div className="flex flex-wrap items-center justify-between gap-3 bg-[#0E2A47] px-5 py-4 text-white">
                <div>
                  <div className="text-lg font-bold">{(preview.ui?.report_title) || 'Customer Report'} — {preview.customer_name}</div>
                  <div className="text-xs text-white/70">{(preview.ui?.generated) || 'Generated'} {new Date(preview.generated_at).toLocaleString()} · {preview.prepared_by || ''}</div>
                </div>
                <div className="flex gap-2">
                  {[['pdf', FileText, 'PDF'], ['xlsx', FileSpreadsheet, 'Excel'], ['docx', FileType2, 'Word']].map(([f, Icon, lbl]) => (
                    <button key={f} onClick={() => doExport(f)} disabled={!!exporting} data-testid={`report-export-${f}`}
                      className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-1.5 text-sm font-semibold text-white hover:bg-white/20 disabled:opacity-60">
                      {exporting === f ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />} {lbl}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-7 p-5">
                {preview.sections.map((b) => {
                  const cnt = sectionCount(b);
                  return (
                    <div key={b.key} data-testid={`report-block-${b.key}`}>
                      <div className="mb-2 border-b border-slate-100 pb-1 text-sm font-bold uppercase tracking-wide text-[#0E2A47]">
                        {b.label}{cnt !== null ? ` (${cnt})` : ''}
                      </div>
                      {b.gap ? (
                        <p className="text-sm text-slate-400" data-testid={`report-gap-${b.key}`}>{b.gap_message || 'No records.'}</p>
                      ) : b.type === 'keyvalue' ? (
                        <div className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                          {b.items.map((i, ix) => (
                            <div key={ix} className="flex flex-col"><span className="text-[11px] uppercase tracking-wide text-slate-400">{i.label}</span><span className="text-sm text-[#0E2A47]">{i.value}</span></div>
                          ))}
                        </div>
                      ) : b.type === 'groups' ? (
                        <div className="space-y-3">
                          {b.groups.map((g, gi) => (
                            <div key={gi}>
                              <div className="text-xs font-bold uppercase text-[#8FC93A]">{g.title}</div>
                              <div className="mt-1 grid gap-x-8 gap-y-1 sm:grid-cols-2">
                                {g.items.map((i, ii) => (
                                  <div key={ii} className="text-sm text-slate-700">{i.label ? <span className="font-semibold">{i.label}: </span> : null}{i.value}</div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : b.type === 'spec_docs' ? (
                        <div className="space-y-5" data-testid="report-spec-docs">
                          {b.entries.map((ent, ei) => (
                            <div key={ei} className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
                              <div className="mb-2 flex flex-wrap items-center gap-2">
                                <span className="text-xs font-bold uppercase text-[#8FC93A]">{ent.property} — {ent.title}</span>
                                {ent.translated && (
                                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                    Machine-translated from {ent.source_language === 'es' ? 'Spanish' : 'English'}
                                  </span>
                                )}
                              </div>
                              {!ent.ok ? (
                                <p className="text-sm text-[#C8641E]">{ent.reason || 'Content unavailable — download to view.'}</p>
                              ) : (
                                <div className="space-y-2">
                                  {ent.blocks.map((blk, bi) => blk.type === 'heading' ? (
                                    <div key={bi} className={`font-bold text-[#0E2A47] ${blk.level <= 1 ? 'text-base mt-2' : 'text-sm'}`}>{blk.text}</div>
                                  ) : blk.type === 'table' ? (
                                    <div key={bi} className="overflow-auto"><table className="min-w-full border border-slate-200 text-xs"><tbody>{blk.rows.map((r, ri) => <tr key={ri} className={ri === 0 ? 'bg-slate-100 font-semibold' : ''}>{r.map((cc, cci) => <td key={cci} className="border border-slate-200 px-2 py-1 align-top text-slate-700">{cc}</td>)}</tr>)}</tbody></table></div>
                                  ) : (
                                    <p key={bi} className="text-sm leading-relaxed text-slate-700">{blk.text}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                          {b.missing && b.missing.map((m, mi) => <p key={mi} className="text-xs text-[#C8641E]">• {m}</p>)}
                        </div>
                      ) : (
                        <div className="overflow-auto rounded-lg border border-slate-100">
                          <table className="min-w-full text-xs">
                            <thead className="bg-[#0E2A47] text-white">
                              <tr>{b.headers.map((head) => <th key={head} className="whitespace-nowrap px-3 py-2 text-left font-semibold">{head}</th>)}</tr>
                            </thead>
                            <tbody>
                              {b.rows.map((r, ri) => (
                                <tr key={ri} className={ri % 2 ? 'bg-slate-50' : 'bg-white'}>
                                  {r.map((c, ci) => <td key={ci} className="whitespace-nowrap px-3 py-1.5 text-slate-700">{c}</td>)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {b.note && <p className="mt-1.5 text-[11px] text-slate-400">{b.note}</p>}
                    </div>
                  );
                })}

                {preview.gaps && preview.gaps.length > 0 && (
                  <div className="rounded-lg border border-orange-100 bg-orange-50 p-3" data-testid="report-gaps-summary">
                    <div className="text-xs font-bold uppercase tracking-wide text-[#C8641E]">{preview.ui?.data_gaps || 'Data Gaps'}</div>
                    <ul className="mt-1 list-inside list-disc text-xs text-[#C8641E]">
                      {preview.gaps.map((g, i) => <li key={i}>{g}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerReportBuilder;
